import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Button, GlareCardRounded, Input, StyleRoot, useHasMounted, useTokenData } from "../../../global-account/react/index.js";
import { baseMainnet } from "../../../shared/constants/chains/supported.js";
import { motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { createPublicClient, encodeFunctionData, formatEther, http, parseEther } from "viem";
import { BondkitToken, BondkitTokenABI } from "../../../bondkit/index.js";
import { AnySpendCustom } from "./AnySpendCustom.js";
// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
// Format number with commas
function formatNumberWithCommas(x) {
  const parts = x.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}
export function AnySpendBondKit({
  mode = "modal",
  recipientAddress,
  contractAddress,
  minTokensOut = "0",
  imageUrl,
  b3Amount: initialB3Amount,
  onSuccess,
  slots,
  content,
  theme,
  showFiatOption
}) {
  const hasMounted = useHasMounted();
  const [showAmountPrompt, setShowAmountPrompt] = useState(!initialB3Amount);
  const [b3Amount, setB3Amount] = useState(initialB3Amount || "");
  const [isAmountValid, setIsAmountValid] = useState(!!initialB3Amount);
  const [validationError, setValidationError] = useState("");
  const [tokenName, setTokenName] = useState("");
  const [tokenSymbol, setTokenSymbol] = useState("");
  const [quote, setQuote] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  // Create BondKit client
  const bondkitTokenClient = useMemo(() => {
    if (!contractAddress) return null;
    try {
      const client = new BondkitToken(contractAddress);
      client.connect();
      return client;
    } catch (error) {
      console.error("Error creating bondkit client", error);
      return null;
    }
  }, [contractAddress]);
  // Create a public client for reading contract data
  const basePublicClient = createPublicClient({
    chain: baseMainnet,
    transport: http()
  });
  // Fetch token name from contract
  useEffect(() => {
    async function fetchTokenName() {
      try {
        const [name, symbol] = await Promise.all([basePublicClient.readContract({
          address: contractAddress,
          abi: BondkitTokenABI,
          functionName: "name"
        }), basePublicClient.readContract({
          address: contractAddress,
          abi: BondkitTokenABI,
          functionName: "symbol"
        })]);
        setTokenName(name);
        setTokenSymbol(symbol);
      } catch (error_0) {
        console.error("Error fetching token name:", error_0);
        setTokenName("BondKit Token");
        setTokenSymbol("BOND");
      }
    }
    if (contractAddress) {
      fetchTokenName();
    }
  }, [contractAddress, basePublicClient]);
  // Get b3 token data
  const {
    data: tokenData,
    isError: isTokenError,
    isLoading
  } = useTokenData(baseMainnet.id, "0xB3B32F9f8827D4634fE7d973Fa1034Ec9fdDB3B3");
  // Convert token data to AnySpend Token type
  const dstToken = useMemo(() => {
    if (!tokenData) return null;
    return {
      address: tokenData.address,
      chainId: baseMainnet.id,
      name: tokenData.name,
      symbol: tokenData.symbol,
      decimals: tokenData.decimals,
      metadata: {
        logoURI: tokenData.logoURI
      }
    };
  }, [tokenData]);
  // Debounced quote fetching
  const debouncedGetQuote = useMemo(() => debounce(async val => {
    if (!val || Number(val) <= 0 || !bondkitTokenClient) {
      setQuote(null);
      return;
    }
    try {
      setIsLoadingQuote(true);
      const parsedAmount = parseEther(val);
      const buyQuote = await bondkitTokenClient.getAmountOfTokensToBuy(parsedAmount);
      setQuote(buyQuote ? formatEther(buyQuote) : null);
    } catch (error_1) {
      console.error("Error getting buy quote:", error_1);
      setQuote(null);
    } finally {
      setIsLoadingQuote(false);
    }
  }, 500), [bondkitTokenClient]);
  // Fetch initial quote if b3Amount is provided
  useEffect(() => {
    if (initialB3Amount && bondkitTokenClient) {
      debouncedGetQuote(initialB3Amount);
    }
  }, [initialB3Amount, bondkitTokenClient, debouncedGetQuote]);
  const validateAndSetAmount = value => {
    // Allow empty input
    if (value === "") {
      setB3Amount("");
      setIsAmountValid(false);
      setValidationError("");
      setQuote(null);
      return;
    }
    // Only allow valid number format (no leading zeros unless decimal)
    if (!/^(0|[1-9]\d*)?\.?\d*$/.test(value)) {
      setIsAmountValid(false);
      setValidationError("Please enter a valid number");
      return;
    }
    // Prevent multiple decimal points
    if ((value.match(/\./g) || []).length > 1) {
      return;
    }
    // Prevent more than 18 decimal places (B3 precision)
    const parts = value.split(".");
    if (parts[1] && parts[1].length > 18) {
      return;
    }
    // Clean the input - remove leading zeros if not decimal
    const cleanedValue = value.startsWith("0") && !value.startsWith("0.") ? value.replace(/^0+/, "0") : value;
    setB3Amount(cleanedValue);
    try {
      const parsedAmount_0 = parseEther(cleanedValue);
      if (parsedAmount_0 <= BigInt(0)) {
        setIsAmountValid(false);
        setValidationError("Amount must be greater than 0");
        return;
      }
      setIsAmountValid(true);
      setValidationError("");
      debouncedGetQuote(cleanedValue);
    } catch (error_2) {
      console.error("Error validating amount:", error_2);
      setIsAmountValid(false);
      setValidationError("Please enter a valid amount");
    }
  };
  const header = () => _jsx("div", {
    className: "bg-as-surface-primary w-full px-6 py-4",
    children: _jsxs("div", {
      className: "flex w-full flex-col items-center space-y-6",
      children: [_jsxs("h2", {
        className: "text-[28px] font-bold",
        children: ["Buy ", tokenName, " (", tokenSymbol, ")"]
      }), _jsxs("div", {
        className: "flex w-full flex-col items-center space-y-2",
        children: [_jsxs("span", {
          className: "text-[28px] font-bold",
          children: [b3Amount, " B3"]
        }), quote && _jsxs("span", {
          className: "text-lg",
          children: ["\u2248 ", formatNumberWithCommas(parseFloat(quote).toFixed(4)), " ", tokenSymbol]
        })]
      })]
    })
  });
  // Show loading state while fetching token data
  if (isLoading) {
    return _jsx(StyleRoot, {
      children: _jsx("div", {
        className: "b3-root b3-modal bg-b3-react-background flex w-full flex-col items-center p-8",
        children: _jsx("p", {
          className: "text-as-primary/70 text-center text-sm",
          children: "Loading payment information..."
        })
      })
    });
  }
  // If we don't have token data after loading, show error state
  if (!dstToken || isTokenError) {
    return _jsx(StyleRoot, {
      children: _jsx("div", {
        className: "b3-root b3-modal bg-b3-react-background flex w-full flex-col items-center p-8",
        children: _jsxs("p", {
          className: "text-as-red text-center text-sm",
          children: ["Failed to fetch native token information for chain ", baseMainnet.id, ". Please try again."]
        })
      })
    });
  }
  if (showAmountPrompt) {
    return _jsx(StyleRoot, {
      children: _jsxs("div", {
        className: "bg-b3-react-background flex w-full flex-col items-center",
        children: [_jsxs("div", {
          className: "w-full px-4 pb-2 pt-4",
          children: [_jsx(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0,
              ease: "easeInOut"
            },
            className: "mb-4 flex justify-center",
            children: imageUrl && _jsxs("div", {
              className: "relative size-16",
              children: [_jsx("div", {
                className: "absolute inset-0 scale-95 rounded-[50%] bg-gradient-to-br from-blue-500/20 to-purple-600/20 blur-xl"
              }), _jsxs(GlareCardRounded, {
                className: "overflow-hidden rounded-full border-none bg-gradient-to-br from-blue-500/10 to-purple-600/10 backdrop-blur-sm",
                children: [_jsx("img", {
                  alt: "token preview",
                  className: "size-full rounded-lg object-cover",
                  src: imageUrl
                }), _jsx("div", {
                  className: "absolute inset-0 rounded-[50%] border border-white/20"
                })]
              })]
            })
          }), _jsx(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0.1,
              ease: "easeInOut"
            },
            className: "text-center",
            children: _jsxs("h2", {
              className: "font-sf-rounded text-as-primary mb-4 text-2xl font-bold",
              children: ["Buy ", tokenName]
            })
          })]
        }), _jsx(motion.div, {
          initial: false,
          animate: {
            opacity: hasMounted ? 1 : 0,
            y: hasMounted ? 0 : 20,
            filter: hasMounted ? "blur(0px)" : "blur(10px)"
          },
          transition: {
            duration: 0.3,
            delay: 0.2,
            ease: "easeInOut"
          },
          className: "bg-b3-react-background w-full p-6",
          children: _jsxs("div", {
            className: "space-y-4",
            children: [_jsx("div", {
              className: "flex items-center justify-between",
              children: _jsx("p", {
                className: "text-as-primary/70 text-sm font-medium",
                children: "B3 Amount"
              })
            }), _jsx("div", {
              className: "relative",
              children: _jsx(Input, {
                type: "text",
                inputMode: "decimal",
                placeholder: "0.1",
                value: b3Amount,
                onChange: e => validateAndSetAmount(e.target.value),
                className: `h-14 px-4 text-lg ${!isAmountValid && b3Amount ? "border-as-red" : "border-b3-react-border"}`
              })
            }), !isAmountValid && b3Amount && _jsx("p", {
              className: "text-as-red text-sm",
              children: validationError
            }), _jsxs("div", {
              className: "bg-as-on-surface-2/30 rounded-lg border border-white/10 p-4 backdrop-blur-sm",
              children: [_jsxs("div", {
                className: "flex items-center justify-between",
                children: [_jsx("span", {
                  className: "text-as-primary/70 text-sm font-medium",
                  children: "Total Cost:"
                }), _jsx("div", {
                  className: "flex items-center gap-2",
                  children: _jsxs("span", {
                    className: "text-as-primary text-lg font-bold",
                    children: [b3Amount || "0", " B3"]
                  })
                })]
              }), isLoadingQuote ? _jsx("div", {
                className: "mt-2 text-center",
                children: _jsx("span", {
                  className: "text-as-primary/70 text-sm",
                  children: "Calculating tokens..."
                })
              }) : quote ? _jsxs("div", {
                className: "mt-2 flex items-center justify-between",
                children: [_jsx("span", {
                  className: "text-as-primary/70 text-sm font-medium",
                  children: "You'll receive:"
                }), _jsxs("span", {
                  className: "text-as-primary text-sm font-medium",
                  children: ["\u2248 ", formatNumberWithCommas(parseFloat(quote).toFixed(4)), " ", tokenSymbol]
                })]
              }) : null]
            }), _jsx(Button, {
              onClick: () => {
                if (isAmountValid && b3Amount) {
                  setShowAmountPrompt(false);
                }
              },
              disabled: !isAmountValid || !b3Amount || isLoadingQuote,
              className: "bg-as-brand hover:bg-as-brand/90 text-as-primary mt-4 h-14 w-full rounded-xl text-lg font-medium",
              children: "Continue"
            })]
          })
        })]
      })
    });
  }
  const encodedData = encodeFunctionData({
    abi: BondkitTokenABI,
    functionName: "buyFor",
    args: [recipientAddress, parseEther(b3Amount), BigInt(minTokensOut)]
  });
  return _jsx(AnySpendCustom, {
    mode: mode,
    recipientAddress: recipientAddress,
    orderType: "custom",
    dstChainId: baseMainnet.id,
    dstToken: dstToken,
    dstAmount: parseEther(b3Amount).toString(),
    contractAddress: contractAddress,
    encodedData: encodedData,
    metadata: {
      action: "BondKit Buy"
    },
    header: header,
    onSuccess: onSuccess,
    showRecipient: true,
    slots: slots,
    content: content,
    theme: theme,
    showFiatOption: showFiatOption
  });
}